import { observable, when } from '@legendapp/state';

import { VerticalBreakpoints } from '../../hooks/use-vertical-breakpoint';

import { info, warning } from '../../utils/errors';

import { TrackingEventName } from './tracking/types';

import { containerStore } from './container-store';
import { DeviceOrientation, DeviceType, deviceSettingStore } from './device-setting-store';
import { disclaimerStoreActions } from './disclaimer-store';
import { trackingStore } from './tracking-store';
import { zoomStore, zoomStoreActions } from './zoom-store';

interface FullscreenStore {
	isFullscreenMode: boolean;
	isFullscreenAvailableOnDevice: boolean;
	isOrientationLockAvailable: boolean;
	isTopHeaderVisible: boolean;
	previousDeviceOrientation: string;
	toggleFullscreen: () => Promise<void>;
}

export const fullscreenStore = observable<FullscreenStore>({
	isFullscreenMode: false,
	isFullscreenAvailableOnDevice: () => setFullscreenAvailability(),
	isOrientationLockAvailable: () => setOrientationLockAvailability(),
	isTopHeaderVisible: () => setTopHeaderVisibility(),
	previousDeviceOrientation: '',
	toggleFullscreen: async (): Promise<void> => {
		await toggleFullscreenHandler();
	}
});

function setFullscreenAvailability(): boolean {
	const container = containerStore.container.get() as HTMLElement;
	return container && typeof container.requestFullscreen === 'function';
}

function setOrientationLockAvailability(): boolean {
	// @ts-expect-error lock is not a property that exists all the time
	return !!screen.orientation && typeof screen.orientation.lock === 'function';
}

function setTopHeaderVisibility(): boolean {
	const { isMobileDevice, deviceType, deviceOrientation, screenHeight } = deviceSettingStore.get();
	const isIpad = deviceType === DeviceType.iPad;
	const isFullscreenMode = fullscreenStore.isFullscreenMode.get();
	const isZoomed = zoomStore.isZoomed.get();

	const isLandscapeOrientation = deviceOrientation === DeviceOrientation.Landscape;
	const smallMobileHeightCondition = screenHeight < VerticalBreakpoints.b340;

	return (
		(isMobileDevice && isLandscapeOrientation && !isIpad && !isZoomed && smallMobileHeightCondition) || isFullscreenMode
	);
}

export async function toggleFullscreenHandler() {
	const container = containerStore.container.get() as HTMLElement;
	const isOrientationLockAvailable = fullscreenStore.isOrientationLockAvailable.get();

	if (!container) return;

	const errorMessage = (msg: string) =>
		`Fullscreen error: ${msg.replace('NotSupportedError', 'Feature not supported')}`;

	const enterFullscreen = async () => {
		fullscreenStore.previousDeviceOrientation.set(screen.orientation.type);
		disclaimerStoreActions.closeDisclaimer();

		await container.requestFullscreen();
		trackingStore.track(TrackingEventName.VISUALIZER_FULLSCREEN_OPEN);

		if (isOrientationLockAvailable) {
			try {
				// @ts-expect-error lock is not a property that exists all the time
				await screen.orientation.lock('landscape');
			} catch (e) {
				info(`Orientation lock failed: ${e}`);
			}
		}
	};

	const exitFullscreen = async () => {
		if (isOrientationLockAvailable) {
			try {
				const prevOrientation = fullscreenStore.previousDeviceOrientation.get() || 'natural';
				// @ts-expect-error lock is not a property that exists all the time
				await screen.orientation.lock(prevOrientation);
			} catch (e) {
				info(`Orientation lock failed: ${e}`);
			}
			try {
				screen.orientation.unlock();
			} catch (e) {
				info(`Orientation unlock failed: ${e}`);
			}
		}
		await document.exitFullscreen();
	};

	try {
		return document.fullscreenElement ? await exitFullscreen() : await enterFullscreen();
	} catch (e) {
		info(e instanceof Error ? errorMessage(e.message) : 'Unknown fullscreen error');
	}
}

when(
	() => containerStore.container.get(),
	() => {
		const handleFullscreenModeChange = () => {
			const container = containerStore.container.get() as HTMLElement;
			const nbaHeight = containerStore.nbaHeight.get();

			if (!container) return;

			zoomStoreActions.resetZoom();

			if (typeof document.exitFullscreen !== 'function') {
				warning('Device does not support fullscreen');
			}

			const isCurrentlyFullscreen = !!document.fullscreenElement;
			fullscreenStore.isFullscreenMode.set(isCurrentlyFullscreen);

			container.style.scrollMarginBottom = `${nbaHeight}px`;
			container.scrollIntoView({ behavior: 'auto', block: 'end', inline: 'nearest' });
		};

		const handleOrientationChange = () => {
			deviceSettingStore.deviceOrientation.get();
		};

		const handleWindowResize = () => {
			containerStore.windowInnerHeight.set(window.innerHeight);
		};

		window.addEventListener('resize', handleWindowResize);
		screen.orientation.addEventListener('change', handleOrientationChange);
		document.addEventListener('fullscreenchange', handleFullscreenModeChange);
	}
);
